import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../../lib/api";
import {
  Plus, Search, Loader2, Pencil, Trash2, Copy, Calendar,
  ArrowLeft, MapPin, MoreVertical, EyeOff, Star, Clock,
  CheckCircle2, AlertCircle, X, Sparkles, ChevronDown,
} from "lucide-react";
import { ConfirmModal } from "../../../components/ConfirmModal";
import ImportPosterModal from "./ImportPosterModal";

/**
 * Events V2 — Admin Dashboard (Beta polish)
 *
 * Visual highlights vs Sprint 1.5:
 *  - Solid pill badges with proper contrast, plus a derived "Programmato"
 *    state for drafts scheduled far in the future.
 *  - Skeleton grid while loading (instead of a spinner in a void).
 *  - Inline toast for success / error (replaces window.alert).
 *  - Menu dropdown with click-outside handling and better positioning.
 *  - "Nascosto al pubblico" hint when the seeded/hidden events are shown.
 *  - Sticky toolbar with counts and clear "reset filtri" affordance.
 */

// ---------------------------------------------------------------------------
// Status meta — colors mapped to Tailwind classes.
// The DB constraint allows: draft, published, archived, cancelled, postponed.
// "programmato" is a UX-derived state (draft + future start date).
// ---------------------------------------------------------------------------
const STATUS_META = {
  draft:       { label: "Bozza",       chip: "bg-slate-100 text-slate-700 ring-slate-200" },
  programmato: { label: "Programmato", chip: "bg-amber-50 text-amber-800 ring-amber-200" },
  published:   { label: "Pubblicato",  chip: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  archived:    { label: "Archiviato",  chip: "bg-zinc-100 text-zinc-600 ring-zinc-200" },
  cancelled:   { label: "Annullato",   chip: "bg-rose-50 text-rose-700 ring-rose-200" },
  postponed:   { label: "Rinviato",    chip: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
};

const HIDDEN_CUTOFF_YEAR = 2098;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const isFutureDate = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getTime() > Date.now(); } catch { return false; }
};

const isHiddenFromPublic = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getUTCFullYear() >= HIDDEN_CUTOFF_YEAR; } catch { return false; }
};

const derivedStatus = (ev) => {
  // Drafts with a fully composed cover + future date are treated as "Programmato".
  if (ev.status === "draft" && ev.cover_image && isFutureDate(ev.start_at)) return "programmato";
  return ev.status || "draft";
};

// ---------------------------------------------------------------------------
// Inline art for the "no events yet" empty state
// ---------------------------------------------------------------------------
const EmptyCalendarArt = () => (
  <svg viewBox="0 0 120 120" className="w-24 h-24 mx-auto" aria-hidden="true">
    <defs>
      <linearGradient id="ec1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#a7f3d0" /><stop offset="1" stopColor="#10b981" />
      </linearGradient>
    </defs>
    <rect x="18" y="26" width="84" height="80" rx="14" fill="#ecfdf5" />
    <rect x="18" y="26" width="84" height="18" rx="14" fill="url(#ec1)" />
    <rect x="34" y="16" width="6" height="20" rx="3" fill="#065f46" />
    <rect x="80" y="16" width="6" height="20" rx="3" fill="#065f46" />
    <circle cx="42" cy="66" r="4" fill="#10b981" />
    <circle cx="60" cy="66" r="4" fill="#10b981" />
    <circle cx="78" cy="66" r="4" fill="#d1fae5" />
    <circle cx="42" cy="84" r="4" fill="#d1fae5" />
    <circle cx="60" cy="84" r="4" fill="#10b981" />
    <circle cx="78" cy="84" r="4" fill="#d1fae5" />
  </svg>
);

// ---------------------------------------------------------------------------
// Inline toast — no external dep, mounts once
// ---------------------------------------------------------------------------
const Toast = ({ toast, onDismiss }) => {
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(onDismiss, 3800);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const tone = toast.tone === "error"
    ? "bg-rose-600 text-white"
    : "bg-slate-900 text-white";
  const Icon = toast.tone === "error" ? AlertCircle : CheckCircle2;
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] pointer-events-auto"
      role="status"
      aria-live="polite"
      data-testid="events-toast"
    >
      <div className={`flex items-center gap-3 rounded-full pl-4 pr-2 py-2 shadow-lg ${tone}`}>
        <Icon className="w-4 h-4 shrink-0" />
        <span className="text-sm">{toast.msg}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded-full hover:bg-white/10"
          aria-label="Chiudi notifica"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton card
// ---------------------------------------------------------------------------
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden animate-pulse">
    <div className="aspect-[16/9] bg-slate-100" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-slate-100 rounded w-3/4" />
      <div className="h-3 bg-slate-100 rounded w-1/2" />
      <div className="h-3 bg-slate-100 rounded w-2/5" />
    </div>
    <div className="px-4 pb-4 flex gap-2">
      <div className="flex-1 h-9 bg-slate-100 rounded-full" />
      <div className="w-9 h-9 bg-slate-100 rounded-full" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Card component
// ---------------------------------------------------------------------------
const EventCard = ({ ev, onDelete, onDuplicate, busyId }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const dStatus = derivedStatus(ev);
  const meta = STATUS_META[dStatus] || STATUS_META.draft;
  const busy = busyId === ev.id;
  const hidden = isHiddenFromPublic(ev.visible_from);

  // click-outside for menu
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  return (
    <div
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col transition-all hover:border-slate-300 hover:shadow-md"
      data-testid={`event-card-${ev.id}`}
    >
      {/* Cover */}
      <Link
        to={`/admin/eventi-v2/${ev.id}`}
        className="relative block aspect-[16/9] bg-slate-100 overflow-hidden"
      >
        {ev.cover_image ? (
          <img
            src={ev.cover_image}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-300">
            <Calendar className="w-10 h-10" />
          </div>
        )}
        {/* Top-left status pill */}
        <span
          className={`absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${meta.chip}`}
        >
          {meta.label}
        </span>
        {/* Featured star */}
        {ev.featured && (
          <span
            className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-amber-400/95 text-amber-900 px-2 py-1 text-[11px] font-semibold shadow-sm"
            title="In evidenza"
          >
            <Star className="w-3 h-3" /> In evidenza
          </span>
        )}
        {/* Hidden-from-public strip */}
        {hidden && (
          <div className="absolute bottom-0 inset-x-0 bg-slate-900/80 text-white text-[11px] font-medium px-3 py-1 flex items-center gap-1.5">
            <EyeOff className="w-3 h-3" /> Nascosto al pubblico
          </div>
        )}
      </Link>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col min-h-[110px]">
        <Link
          to={`/admin/eventi-v2/${ev.id}`}
          className="font-semibold text-slate-900 hover:text-emerald-700 leading-snug line-clamp-2"
        >
          {ev.title}
        </Link>
        <div className="mt-2.5 space-y-1.5 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 shrink-0" />
            <span>{formatDate(ev.start_at)}</span>
          </div>
          {(ev.location_name || ev.city) && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{ev.location_name || ev.city}</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex items-center gap-2 relative">
        <Link
          to={`/admin/eventi-v2/${ev.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-sm font-medium text-white transition"
          data-testid={`edit-event-${ev.id}`}
        >
          <Pencil className="w-3.5 h-3.5" /> Modifica
        </Link>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            disabled={busy}
            className="p-2 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-500 disabled:opacity-40"
            aria-label="Altre azioni"
            aria-expanded={menuOpen}
            data-testid={`menu-event-${ev.id}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 bottom-11 z-20 w-48 rounded-xl border border-slate-200 bg-white shadow-xl py-1 animate-in fade-in zoom-in-95"
              role="menu"
            >
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => { setMenuOpen(false); onDuplicate(ev); }}
                data-testid={`duplicate-event-${ev.id}`}
              >
                <Copy className="w-4 h-4" /> Duplica evento
              </button>
              <div className="h-px bg-slate-100 my-1" />
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
                onClick={() => { setMenuOpen(false); onDelete(ev); }}
                data-testid={`delete-event-${ev.id}`}
              >
                <Trash2 className="w-4 h-4" /> Elimina
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------
export default function EventsDashboardV2() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortDir, setSortDir] = useState("desc");
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  // Confirmation modal state
  const [modal, setModal] = useState({ open: false });
  const [importOpen, setImportOpen] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuRef = useRef(null);

  useEffect(() => {
    if (!newMenuOpen) return undefined;
    const handler = (e) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target)) setNewMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [newMenuOpen]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/events");
      setRows(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore nel caricamento eventi");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (statusFilter !== "all") {
      // Filter by the same derived state used for the badges/counters so the
      // chip counter and the filtered grid stay in sync.
      out = out.filter((r) => derivedStatus(r) === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((r) => (r.title || "").toLowerCase().includes(q));
    }
    out = [...out].sort((a, b) => {
      const av = a.start_at ? new Date(a.start_at).getTime() : 0;
      const bv = b.start_at ? new Date(b.start_at).getTime() : 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return out;
  }, [rows, search, statusFilter, sortDir]);

  // Counts per status (for chips)
  const counts = useMemo(() => {
    const c = { all: rows.length, draft: 0, programmato: 0, published: 0, archived: 0 };
    for (const r of rows) {
      const ds = derivedStatus(r);
      if (ds === "programmato") { c.programmato += 1; continue; }
      if (r.status && c[r.status] !== undefined) c[r.status] += 1;
    }
    return c;
  }, [rows]);

  const askDelete = (ev) => setModal({
    open: true, kind: "delete", ev,
    title: "Elimina evento",
    message: `Sicuro di voler eliminare "${ev.title}"?\nVerrà spostato nel cestino e sarà recuperabile.`,
    confirmLabel: "Elimina",
    tone: "danger",
  });

  const askDuplicate = (ev) => setModal({
    open: true, kind: "duplicate", ev,
    title: "Duplica evento",
    message: `Verrà creata una copia in bozza di "${ev.title}". Potrai modificare data, orario e locandina per riutilizzare l'impianto dell'evento.`,
    confirmLabel: "Duplica",
    tone: "primary",
  });

  const runModal = async () => {
    if (!modal.open || busyId) return;
    setBusyId(modal.ev.id);
    try {
      if (modal.kind === "delete") {
        await api.delete(`/admin/events/${modal.ev.id}`);
        setRows((prev) => prev.filter((r) => r.id !== modal.ev.id));
        setToast({ tone: "success", msg: "Evento eliminato" });
      } else if (modal.kind === "duplicate") {
        const r = await api.post(`/admin/events/${modal.ev.id}/duplicate`, {});
        setModal({ open: false });
        setBusyId(null);
        setToast({ tone: "success", msg: "Evento duplicato — apri per modificarlo" });
        navigate(`/admin/eventi-v2/${r.data.id}`);
        return;
      }
      setModal({ open: false });
    } catch (e) {
      setToast({ tone: "error", msg: e?.response?.data?.detail || e.message || "Errore" });
    } finally { setBusyId(null); }
  };

  const isEmptyUnfiltered = !loading && rows.length === 0;
  const activeFilter = search.trim() || statusFilter !== "all";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="events-v2-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition"
          data-testid="back-to-admin"
        >
          <ArrowLeft className="w-4 h-4" /> Torna al pannello
        </Link>

        {/* Header */}
        <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Eventi V2 · Beta
            </div>
            <h1 className="mt-1 text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
              Gestione eventi
            </h1>
            <p className="mt-1 text-sm text-slate-500 max-w-lg">
              Il nuovo pannello per creare, duplicare e programmare gli eventi di Sanluri.
              Backup completo del modulo V10, nessun impatto sul sito pubblico.
            </p>
          </div>
          <div className="self-start md:self-end relative" ref={newMenuRef}>
            <div className="inline-flex items-center rounded-full bg-emerald-600 text-white shadow-sm hover:shadow-md transition overflow-hidden">
              <Link
                to="/admin/eventi-v2/nuovo"
                data-testid="new-event-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 font-medium hover:bg-emerald-700 transition"
              >
                <Plus className="w-4 h-4" /> Nuovo evento
              </Link>
              <button
                type="button"
                onClick={() => setNewMenuOpen((v) => !v)}
                data-testid="new-event-menu-toggle"
                aria-label="Altre opzioni di creazione"
                aria-expanded={newMenuOpen}
                className="border-l border-emerald-500/50 px-2.5 py-2.5 hover:bg-emerald-700 transition"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${newMenuOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            {newMenuOpen && (
              <div
                className="absolute right-0 top-12 z-20 w-64 rounded-2xl border border-slate-200 bg-white shadow-xl py-1.5 animate-in fade-in zoom-in-95 origin-top-right"
                role="menu"
                data-testid="new-event-menu"
              >
                <Link
                  to="/admin/eventi-v2/nuovo"
                  onClick={() => setNewMenuOpen(false)}
                  className="flex items-start gap-3 px-3.5 py-2.5 hover:bg-slate-50 rounded-lg mx-1"
                  data-testid="new-event-manual"
                >
                  <Plus className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">Crea manualmente</div>
                    <div className="text-xs text-slate-500 leading-tight">Compila i campi da zero</div>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => { setNewMenuOpen(false); setImportOpen(true); }}
                  className="w-full flex items-start gap-3 px-3.5 py-2.5 hover:bg-emerald-50 rounded-lg mx-1 text-left"
                  data-testid="new-event-ai-import"
                >
                  <Sparkles className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                      Importa da locandina
                      <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">AI</span>
                    </div>
                    <div className="text-xs text-slate-500 leading-tight">Carica JPG · PNG · WEBP · PDF</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Toolbar (hidden in true empty state) */}
        {!isEmptyUnfiltered && (
          <div className="mt-7 space-y-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1 min-w-0">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cerca eventi per titolo…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-full border border-slate-200 bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition"
                  data-testid="events-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 transition"
                  data-testid="events-sort-toggle"
                  aria-label="Ordina per data"
                >
                  <Clock className="w-3.5 h-3.5" />
                  {sortDir === "asc" ? "Più vecchi" : "Più recenti"}
                </button>
              </div>
            </div>

            {/* Status chips */}
            <div className="flex flex-wrap items-center gap-2" data-testid="events-status-filter">
              {[
                { v: "all",         label: "Tutti" },
                { v: "draft",       label: "Bozze" },
                { v: "programmato", label: "Programmati" },
                { v: "published",   label: "Pubblicati" },
                { v: "archived",    label: "Archiviati" },
              ].map((s) => {
                const active = statusFilter === s.v;
                return (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => setStatusFilter(s.v)}
                    className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium transition ring-1 ring-inset ${
                      active
                        ? "bg-slate-900 text-white ring-slate-900"
                        : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`status-chip-${s.v}`}
                  >
                    {s.label}
                    <span className={`text-[10px] px-1.5 rounded-full ${
                      active ? "bg-white/15" : "bg-slate-100 text-slate-500"
                    }`}>
                      {counts[s.v] ?? 0}
                    </span>
                  </button>
                );
              })}
              {activeFilter && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setStatusFilter("all"); }}
                  className="ml-auto text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
                >
                  Azzera filtri
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="mt-6">
          {loading ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
              data-testid="events-loading-skeleton"
            >
              {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : error ? (
            <div className="p-6 bg-rose-50 border border-rose-200 rounded-2xl text-sm text-rose-800 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">Errore nel caricamento</div>
                <div className="mt-0.5">{error}</div>
              </div>
              <button
                type="button"
                onClick={load}
                className="text-xs text-rose-700 underline underline-offset-2"
              >
                Riprova
              </button>
            </div>
          ) : isEmptyUnfiltered ? (
            <div
              className="bg-white rounded-3xl border border-slate-200 py-16 px-6 text-center"
              data-testid="events-empty-state"
            >
              <EmptyCalendarArt />
              <h2 className="mt-6 text-xl font-semibold text-slate-900">Nessun evento ancora</h2>
              <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                Inizia a raccontare quello che accade a Sanluri. Crea la tua prima bozza in meno di
                3 minuti — potrai pubblicarla quando vorrai.
              </p>
              <Link
                to="/admin/eventi-v2/nuovo"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-6 py-3 font-medium hover:bg-emerald-700 transition"
                data-testid="empty-create-btn"
              >
                <Plus className="w-4 h-4" /> Crea il primo evento
              </Link>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="bg-white rounded-3xl border border-slate-200 p-14 text-center">
              <Search className="w-8 h-8 mx-auto text-slate-300 mb-3" />
              <div className="text-slate-700 font-medium">Nessun evento trovato</div>
              <div className="text-sm text-slate-500 mt-1">
                Prova a rivedere i filtri o azzerali per vedere tutti gli eventi.
              </div>
              <button
                type="button"
                onClick={() => { setSearch(""); setStatusFilter("all"); }}
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:underline"
              >
                Azzera filtri
              </button>
            </div>
          ) : (
            <>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                data-testid="events-grid"
              >
                {filteredRows.map((ev) => (
                  <EventCard
                    key={ev.id}
                    ev={ev}
                    onDelete={askDelete}
                    onDuplicate={askDuplicate}
                    busyId={busyId}
                  />
                ))}
              </div>
              <div className="mt-8 text-xs text-slate-400 text-center">
                {filteredRows.length} {filteredRows.length === 1 ? "evento" : "eventi"} · Ordinamento
                per data {sortDir === "asc" ? "crescente" : "decrescente"}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation modal */}
      <ConfirmModal
        open={!!modal.open}
        title={modal.title}
        message={modal.message}
        confirmLabel={modal.confirmLabel}
        tone={modal.tone}
        busy={!!busyId}
        onCancel={() => busyId ? null : setModal({ open: false })}
        onConfirm={runModal}
      />

      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <ImportPosterModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onCompile={(auditId) => {
          setImportOpen(false);
          navigate(`/admin/eventi-v2/nuovo?import=${auditId}`);
        }}
      />
    </div>
  );
}
